import { useEffect, useRef, useState } from "react";
import { motion, animate, useInView } from "framer-motion";
import { cn } from "@/lib/utils";

const EASE = [0.16, 1, 0.3, 1];

/* ---------- Buttons (class strings) ---------- */
export const goldBtn =
  "group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-navy-900 shadow-[0_0_30px_rgba(255,215,0,0.25)] transition-[background-color,transform,box-shadow] duration-300 hover:bg-white hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(255,215,0,0.5)]";

export const ghostBtn =
  "group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white transition-[color,border-color,transform] duration-300 hover:border-gold hover:text-gold hover:-translate-y-0.5";

/* ---------- Layout ---------- */
export const Container = ({ className, children }) => (
  <div className={cn("max-w-7xl mx-auto px-6 md:px-12", className)}>{children}</div>
);

export const Section = ({ id, className, children, ...rest }) => (
  <section id={id} className={cn("py-24 md:py-28 relative", className)} {...rest}>
    {children}
  </section>
);

export const Eyebrow = ({ children, className }) => (
  <span
    className={cn(
      "font-mono-jb text-xs md:text-sm tracking-[0.28em] uppercase text-gold/80 inline-flex items-center gap-3",
      className
    )}
  >
    <span className="h-px w-8 bg-gold/50" />
    {children}
  </span>
);

/* ---------- Motion primitives ---------- */
export const Reveal = ({ children, className, delay = 0, y = 26, as = "div" }) => {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.75, delay, ease: EASE }}
    >
      {children}
    </MotionTag>
  );
};

export const MaskedLines = ({ lines, className, lineClassName, delay = 0 }) => (
  <span className={cn("block", className)}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em]">
        <motion.span
          className={cn("block", lineClassName)}
          initial={{ y: "115%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.95, delay: delay + i * 0.11, ease: EASE }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

export const CountUp = ({ to, prefix = "", suffix = "", decimals = 0, duration = 1.8, className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: EASE,
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to, duration]);

  const display =
    decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString();

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export const Marquee = ({ items, itemClassName, className, separator = true }) => (
  <div className={cn("relative overflow-hidden", className)}>
    <div className="flex w-max animate-marquee items-center gap-16 md:gap-24 will-change-transform">
      {[...items, ...items].map((it, i) => (
        <span key={i} className={cn("flex items-center gap-16 md:gap-24 shrink-0", itemClassName)}>
          {it}
          {separator && <span className="h-1.5 w-1.5 rounded-full bg-gold/60" />}
        </span>
      ))}
    </div>
  </div>
);

/* ---------- Decorative glow ---------- */
export const GlowOrb = ({ className, style }) => (
  <div
    aria-hidden
    className={cn("pointer-events-none absolute rounded-full gold-radial blur-2xl", className)}
    style={style}
  />
);
